/**
 * The Chrome extension gets its sign-in from here.
 *
 * There is one place a person signs in to JobCork: this app. The extension
 * never talks to Auth0. When the extension is installed, this module hands
 * it the same access token the app uses, keeps a port open so the
 * extension can ask for a fresh one before the old one expires, and tells
 * it when the person signs out. The extension keeps the token in session
 * memory only.
 *
 * Which extension: the ids in VITE_EXTENSION_IDS (the development build
 * has a fixed id from the key in its manifest; the Web Store one is added
 * when it exists). Chrome only exposes `chrome.runtime.sendMessage` on this
 * page to extensions that name this origin in externally_connectable, and
 * delivers a message addressed to an id to that extension alone -- so the
 * token can only reach an extension we named.
 */

interface Port {
  postMessage(msg: unknown): void;
  disconnect(): void;
  onMessage: { addListener(fn: (msg: unknown) => void): void };
  onDisconnect: { addListener(fn: () => void): void };
}

interface ExternalRuntime {
  sendMessage(extensionId: string, msg: unknown, callback: (reply: unknown) => void): void;
  connect(extensionId: string, info: { name: string }): Port;
  lastError?: { message?: string };
}

export interface Who {
  name: string | null;
  email: string | null;
}

/** What the extension says about Auto in this browser. Never the key. */
export interface RunnerSummary {
  connected: boolean;
  label: string | null;
  since: number | null;
  line: string;
  dryRun: boolean;
}

export interface ExtensionStatus {
  /** An extension we named answered hello. */
  installed: boolean;
  id: string | null;
  version: string | null;
  /** The port is open and a token has been handed over. */
  connected: boolean;
  /** Auto (E14): whether this browser's extension holds the runner's key, and what it is doing. */
  runner: RunnerSummary | null;
}

const PORT_NAME = "jobcork";
const HELLO_TIMEOUT_MS = 1_000;

export const EXTENSION_IDS: readonly string[] = ((import.meta.env.VITE_EXTENSION_IDS as string | undefined) ?? "")
  .split(",")
  .map((s) => s.trim())
  .filter((s) => /^[a-p]{32}$/.test(s));

function runtime(): ExternalRuntime | null {
  const c = (window as unknown as { chrome?: { runtime?: Partial<ExternalRuntime> } }).chrome;
  const r = c?.runtime;
  return r && typeof r.sendMessage === "function" && typeof r.connect === "function" ? (r as ExternalRuntime) : null;
}

function hello(rt: ExternalRuntime, id: string): Promise<{ version: string; connected: boolean; runner: RunnerSummary | null } | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), HELLO_TIMEOUT_MS);
    try {
      rt.sendMessage(id, { type: "jobcork.hello" }, (reply) => {
        clearTimeout(timer);
        // lastError is set when no extension with that id is listening; reading it also silences Chrome's console warning.
        if (rt.lastError || typeof reply !== "object" || reply === null) { resolve(null); return; }
        const r = reply as { ok?: boolean; version?: string; connected?: boolean; runner?: RunnerSummary | null };
        resolve(r.ok ? { version: String(r.version ?? ""), connected: r.connected === true, runner: r.runner ?? null } : null);
      });
    } catch {
      clearTimeout(timer);
      resolve(null);
    }
  });
}

/** Whether an extension we named is installed here. Cheap; safe to call on every load. */
export async function findExtension(): Promise<ExtensionStatus> {
  const none: ExtensionStatus = { installed: false, id: null, version: null, connected: false, runner: null };
  const rt = runtime();
  if (!rt || EXTENSION_IDS.length === 0) return none;
  for (const id of EXTENSION_IDS) {
    const r = await hello(rt, id);
    if (r) return { installed: true, id, version: r.version, connected: r.connected, runner: r.runner };
  }
  return none;
}

/**
 * Hand the extension the runner's key (Auto, E14), or take it away with
 * null. The key was issued by the API to the signed-in person a moment ago
 * and is shown to nobody; it goes straight from this page to the extension
 * we named. Resolves false when no extension took it.
 */
export function handRunnerKey(key: string | null, label?: string): Promise<boolean> {
  const rt = runtime();
  if (!rt) return Promise.resolve(false);
  return (async () => {
    for (const id of EXTENSION_IDS) {
      const took = await new Promise<boolean>((resolve) => {
        const timer = setTimeout(() => resolve(false), HELLO_TIMEOUT_MS);
        try {
          rt.sendMessage(id, { type: "jobcork.runner-key", key, label }, (reply) => {
            clearTimeout(timer);
            if (rt.lastError) { resolve(false); return; }
            resolve((reply as { ok?: boolean } | null)?.ok === true);
          });
        } catch {
          clearTimeout(timer);
          resolve(false);
        }
      });
      if (took) return true;
    }
    return false;
  })();
}

/** The exp claim, in milliseconds, or null. The extension re-derives it; this is a courtesy. */
export function tokenExpiry(jwt: string): number | null {
  const part = jwt.split(".")[1];
  if (!part) return null;
  try {
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (part.length % 4)) % 4));
    const exp = (JSON.parse(json) as { exp?: unknown }).exp;
    return typeof exp === "number" ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Hand the extension the token and keep answering its requests for a
 * fresh one until `stop` is called. Reconnects if the extension's
 * background restarts (Chrome stops idle service workers; the port drops
 * when it does).
 */
export function connectExtension(
  getToken: () => Promise<string>,
  who: Who,
  onChange: (status: ExtensionStatus) => void,
): () => void {
  let stopped = false;
  let port: Port | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let backoff = 2_000;

  const send = async (p: Port) => {
    const token = await getToken();
    p.postMessage({ type: "jobcork.token", token, expiresAt: tokenExpiry(token), who });
  };

  const open = async () => {
    if (stopped) return;
    const found = await findExtension();
    if (stopped) return;
    if (!found.installed || !found.id) { onChange(found); return; }
    const rt = runtime();
    if (!rt) { onChange(found); return; }
    try {
      const p = rt.connect(found.id, { name: PORT_NAME });
      port = p;
      p.onMessage.addListener((msg) => {
        const m = msg as { type?: string; ok?: boolean };
        if (m?.type === "jobcork.token-request") { send(p).catch(() => { /* the extension keeps what it has */ }); return; }
        if (m?.type === "jobcork.ack" && m.ok) { backoff = 2_000; onChange({ ...found, connected: true }); }
      });
      p.onDisconnect.addListener(() => {
        port = null;
        if (stopped) return;
        onChange({ ...found, connected: false });
        retryTimer = setTimeout(() => { void open(); }, backoff);
        backoff = Math.min(backoff * 2, 60_000);
      });
      await send(p);
    } catch {
      onChange({ ...found, connected: false });
    }
  };

  // A page going into the back/forward cache takes its port with it, and the
  // extension is told so as an error; close it first, cleanly. The
  // onDisconnect handler above reopens it when the page comes back.
  const onPageHide = () => { try { port?.disconnect(); } catch { /* already gone */ } };
  window.addEventListener("pagehide", onPageHide);

  void open();

  return () => {
    stopped = true;
    clearTimeout(retryTimer);
    window.removeEventListener("pagehide", onPageHide);
    try { port?.disconnect(); } catch { /* already gone */ }
    port = null;
  };
}

/** Tell every extension we named that the person signed out, so it drops its token. Best effort. */
export function notifyExtensionSignedOut(): void {
  const rt = runtime();
  if (!rt) return;
  for (const id of EXTENSION_IDS) {
    try { rt.sendMessage(id, { type: "jobcork.signed-out" }, () => { void rt.lastError; }); } catch { /* not installed */ }
  }
}
